import { useState, useEffect } from 'react';
import { Invoice } from '@/types/payments';
import { BankAccountSummary } from '@/types/accounts';
import { ServiceRegistry } from '@/services/ServiceRegistry';
import { useAuth } from '@/context/AuthContext';
import { toast } from "sonner";
import { useTranslations } from 'use-intl';
import { useLatest } from '@/hooks/useLatest';
import { runRefreshWithFeedback } from "@/lib/refreshFeedback";
import ErpService from '@/services/erpService';
import { isErpConnectionEnabled } from '@/config/features';
import { useConsentContextOptional } from '@/components/providers/ConsentProvider';
import { renewalRequestIfAllAccessLost } from '@/lib/renewalRequest';
import { useAccountsAfterRenewal } from '@/hooks/useAccountsAfterRenewal';

interface OverviewData {
  bankAccounts: BankAccountSummary[];
  payables: Invoice[];
  loadingAccounts: boolean;
  loadingPayables: boolean;
  refreshData: () => Promise<void>;
  refreshing: boolean;
}

export const useOverviewData = (): OverviewData => {
  const t = useTranslations('consultantToasts');
  const tRef = useLatest(t);
  const { user } = useAuth();
  const setConsentRequiredError = useConsentContextOptional()?.setConsentRequiredError;
  
  const [bankAccounts, setBankAccounts] = useState<BankAccountSummary[]>([]);
  useAccountsAfterRenewal(setBankAccounts);
  const [payables, setPayables] = useState<Invoice[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState<boolean>(true);
  const [loadingPayables, setLoadingPayables] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);

  // Load bank accounts
  useEffect(() => {
    const fetchBankAccounts = async () => {
      if (!user) {
        setLoadingAccounts(false);
        return;
      }
      
      setLoadingAccounts(true);
      try {
        const response = await ServiceRegistry.getAccountService().getBankAccounts();
        
        if (response && response.length && response.length > 0) {
          setBankAccounts(response);
          const renewal = renewalRequestIfAllAccessLost(response);
          if (renewal) setConsentRequiredError?.(renewal);
        } else {
          setBankAccounts([]);
        }
      } catch (error) {
        console.error("Error loading bank accounts:", error);
        setBankAccounts([]);
      } finally {
        setLoadingAccounts(false);
      }
    };

    fetchBankAccounts();
  }, [user]);

  // Load payables
  useEffect(() => {
    const fetchPayables = async () => {
      if (!user) {
        setLoadingPayables(false);
        return;
      }
      
      setLoadingPayables(true);
      try {
        const payablesData = await ServiceRegistry.getPaymentService().fetchPayables();
        setPayables(payablesData);
      } catch (error) {
        console.error("Error loading payables:", error);
        toast.error(tRef.current('common.error'), { description: tRef.current('loading.payablesFailed') });
        setPayables([]);
      } finally {
        setLoadingPayables(false);
      }
    };

    fetchPayables();
  }, [user, tRef]);

  // Background ERP sync on page load
  useEffect(() => {
    const syncErpData = async () => {
      if (!user || !isErpConnectionEnabled()) {
        return;
      }

      try {
        console.log('Triggering background ERP sync...');
        const erpService = new ErpService();
        await erpService.syncDataBackground();
        // Silent operation - no user feedback needed
        // The sync will be throttled on the backend (20 min)
      } catch (error) {
        // Silent failure - don't disrupt user experience
        console.error('Background ERP sync failed:', error);
      }
    };

    syncErpData();
  }, [user]);

  const refreshData = async () => {
    if (refreshing) return;

    setRefreshing(true);
    try {
      await runRefreshWithFeedback(async () => {
        // Refresh accounts data
        setLoadingAccounts(true);
        try {
          const accountsResponse = await ServiceRegistry.getAccountService().getBankAccounts();
          setBankAccounts(accountsResponse || []);
        } finally {
          setLoadingAccounts(false);
        }

        // Refresh payables data
        setLoadingPayables(true);
        const payablesData = await ServiceRegistry.getPaymentService().fetchPayables();
        setPayables(payablesData);
        setLoadingPayables(false);

        // Background ERP sync (throttled on backend)
        if (isErpConnectionEnabled()) {
          try {
            console.log('Triggering background ERP sync during refresh...');
            const erpService = new ErpService();
            await erpService.syncDataBackground();
          } catch (erpError) {
            // Silent failure for ERP sync - don't disrupt main refresh
            console.error('Background ERP sync failed during refresh:', erpError);
          }
        }

        toast.success(tRef.current('refresh.completeTitle'), { description: tRef.current('refresh.defaultComplete') });
      });
    } finally {
      setRefreshing(false);
    }
  };

  return {
    bankAccounts,
    payables,
    loadingAccounts,
    loadingPayables,
    refreshData,
    refreshing,
  };
};
